// src/components/exam-player/renderers/DragToGroupRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function DragToGroupRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const groups = Array.isArray(item?.body?.groups) ? item.body.groups : [];
  const items = Array.isArray(item?.body?.items) ? item.body.items : [];
  const layout = item?.body?.layout || "top-bottom";

  // ---- Klass بندی لایه‌بندی ----
  const isVertical =
    layout === "top-bottom" || layout === "bottom-top";
  const groupsFirst =
    layout === "bottom-top" || layout === "left-right";

  // ---- مقدار اولیه از initialAnswers ----
  const initialMap = useMemo(() => {
    if (!initialAnswers || typeof initialAnswers !== "object") return {};
    if (
      initialAnswers.assignments &&
      typeof initialAnswers.assignments === "object"
    ) {
      return { ...initialAnswers.assignments };
    }
    return { ...initialAnswers };
  }, [initialAnswers]);

  const [assignments, setAssignments] = useState(initialMap);

  useEffect(() => {
    setAssignments(initialMap);
  }, [initialMap]);

  const handleAssign = (itemKey, groupKeyOrNull) => {
    setAssignments((prev) => {
      const next = { ...prev };
      if (groupKeyOrNull) next[itemKey] = groupKeyOrNull;
      else delete next[itemKey];
      onAnswerChange?.(next); // ← همون map را به بالا می‌فرستیم
      return next;
    });
  };

  // ---- Drag & Drop ----
  const [draggingKey, setDraggingKey] = useState(null);

  const onDragStart = (e, itemKey) => {
    e.dataTransfer.setData("text/plain", itemKey);
    setDraggingKey(itemKey);
  };

  const onDragEnd = () => {
    setDraggingKey(null);
  };

  const onDropToGroup = (e, groupKey) => {
    e.preventDefault();
    const key =
      e.dataTransfer.getData("text/plain") || draggingKey;
    if (!key) return;
    handleAssign(key, groupKey);
  };

  const onDropToUnassigned = (e) => {
    e.preventDefault();
    const key =
      e.dataTransfer.getData("text/plain") || draggingKey;
    if (!key) return;
    handleAssign(key, null);
  };

  const allowDrop = (e) => e.preventDefault();

  // ---- آیتم‌ها بر اساس assignment ----
  const itemsByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => map.set(String(g.key), []));
    const unassigned = [];
    items.forEach((it) => {
      const k = String(it.key);
      const gk = assignments[k];
      if (gk && map.has(String(gk))) {
        map.get(String(gk)).push(it);
      } else {
        unassigned.push(it);
      }
    });
    return { byGroup: map, unassigned };
  }, [items, groups, assignments]);

  if (!groups.length || !items.length) {
    return (
      <div className="text-sm text-red-600">
        {t("preview.kind_not_supported", { kind: "drag_to_group" })}
      </div>
    );
  }

  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  const Container = ({ children }) => {
    if (isVertical) {
      return (
        <div className="flex flex-col gap-4 mt-2">{children}</div>
      );
    }
    return (
      <div className="flex flex-row flex-wrap gap-4 mt-2">
        {children}
      </div>
    );
  };

  const groupsSection = (
    <div className="flex flex-wrap gap-3">
      {groups.map((g) => {
        const gKey = String(g.key);
        const label =
          g?.label?.fa || g?.label?.en || gKey;
        const groupItems =
          itemsByGroup.byGroup.get(gKey) || [];

        return (
          <div
            key={gKey}
            className="min-w-[180px] border rounded-lg p-2 bg-gray-50"
            onDragOver={allowDrop}
            onDrop={(e) => onDropToGroup(e, gKey)}
          >
            <div className="text-xs font-semibold mb-1">
              {label}
            </div>
            <div className="space-y-1 min-h-[40px]">
              {groupItems.map((it) => {
                const itemKey = String(it.key);
                const itemLabel =
                  it?.label?.fa ||
                  it?.label?.en ||
                  itemKey;
                return (
                  <div
                    key={itemKey}
                    draggable
                    onDragStart={(e) =>
                      onDragStart(e, itemKey)
                    }
                    onDragEnd={onDragEnd}
                    className="px-2 py-1 rounded bg-white border text-xs cursor-move"
                  >
                    {itemLabel}
                  </div>
                );
              })}
              {!groupItems.length && (
                <div className="text-[11px] text-gray-400">
                  {t(
                    "drag_to_group.empty_group",
                    "آیتمی در این گروه نیست"
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const unassignedSection = (
    <div
      className="border rounded-lg p-2 bg-white"
      onDragOver={allowDrop}
      onDrop={onDropToUnassigned}
    >
      <div className="text-xs font-semibold mb-1">
        {t(
          "drag_to_group.unassigned",
          "آیتم‌های بدون گروه"
        )}
      </div>
      <div className="flex flex-wrap gap-2 min-h-[40px]">
        {itemsByGroup.unassigned.map((it) => {
          const itemKey = String(it.key);
          const itemLabel =
            it?.label?.fa ||
            it?.label?.en ||
            itemKey;
          return (
            <div
              key={itemKey}
              draggable
              onDragStart={(e) =>
                onDragStart(e, itemKey)
              }
              onDragEnd={onDragEnd}
              className="px-2 py-1 rounded bg-gray-50 border text-xs cursor-move"
            >
              {itemLabel}
            </div>
          );
        })}
        {!itemsByGroup.unassigned.length && (
          <div className="text-[11px] text-gray-400">
            {t(
              "drag_to_group.no_unassigned",
              "همهٔ آیتم‌ها در گروه‌ها قرار دارند"
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* Stem */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {/* {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", { n: questionNumber })}
          </div>
        )} */}
      </div>

      <Container>
        {groupsFirst ? (
          <>
            {groupsSection}
            {unassignedSection}
          </>
        ) : (
          <>
            {unassignedSection}
            {groupsSection}
          </>
        )}
      </Container>
    </div>
  );
}
